import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import CopyButton from '../../servicio/CopyButton';

const TabGeneral = ({ rnc, onEdit, onAsignarFolios }) => {
    if (!rnc) return null;

    const tipoContribuyenteMap = {
        'PERSONA_FISICA': 'Persona Física',
        'PERSONA_JURIDICA': 'Persona Jurídica',
        'REGIMEN_SIMPLIFICADO': 'Régimen Simplificado',
        'GRAN_CONTRIBUYENTE': 'Gran Contribuyente'
    };

    const ambienteMap = {
        0: 'Desarrollo',
        1: 'Test',
        2: 'Certificación',
        3: 'Producción'
    };

    const getAmbienteColor = (ambiente) => {
        const colores = {
            0: 'secondary',
            1: 'info',
            2: 'warning',
            3: 'success'
        };
        return colores[ambiente] || 'secondary';
    };

    const handleDescargarCertificado = () => {
        if (!rnc.vCertificadoDGII) {
            alert('No hay certificado disponible para descargar');
            return;
        }

        try {
            // Convertir base64 a blob
            const byteCharacters = atob(rnc.vCertificadoDGII);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/x-pkcs12' });

            // Crear enlace de descarga
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Certificado_${rnc.vRNC}.pfx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar certificado:', error);
            alert('Error al descargar el certificado. Por favor, intente nuevamente.');
        }
    };

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{rnc.vNombre}</h5>
                <Badge bg={rnc.bIsActive ? 'success' : 'danger'}>
                    {rnc.bIsActive ? 'Activo' : 'Inactivo'}
                </Badge>
            </div>

            {/* Información del Cliente Asociado */}
            {(rnc.idClienteAkasia || rnc.nombreLicenciatario) && (
                <Card className="border-0 bg-light mb-3">
                    <Card.Body>
                        <h6 className="text-primary mb-3">
                            <FontAwesomeIcon icon="user" className="me-2" />
                            Cliente Asociado
                        </h6>

                        {rnc.idClienteAkasia && (
                            <div className="info-item mb-3 pb-3 border-bottom">
                                <label className="text-muted small mb-1">ID Cliente Akasia</label>
                                <div className="fw-semibold">
                                    <code className="bg-white px-2 py-1 rounded">#{rnc.idClienteAkasia}</code>
                                </div>
                            </div>
                        )}

                        {rnc.nombreLicenciatario && (
                            <div className="info-item">
                                <label className="text-muted small mb-1">Nombre del Licenciatario</label>
                                <div className="fw-semibold">{rnc.nombreLicenciatario}</div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Información Básica */}
            <Card className="border-0 bg-light mb-3">
                <Card.Body>
                    <h6 className="text-primary mb-3">
                        <FontAwesomeIcon icon="building" className="me-2" />
                        Información Básica
                    </h6>

                    <div className="info-item mb-3 pb-3 border-bottom">
                        <label className="text-muted small mb-1">RNC</label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="fw-semibold flex-grow-1">
                                <code className="bg-white px-2 py-1 rounded fs-1">{rnc.vRNC}</code>
                            </div>
                            <CopyButton code={rnc.vRNC} />
                        </div>
                    </div>

                    <div className="info-item mb-3 pb-3 border-bottom">
                        <label className="text-muted small mb-1">Razón Social / Nombre Comercial</label>
                        <div className="fw-semibold">{rnc.vNombre}</div>
                    </div>

                    {rnc.vDireccionFiscal && (
                        <div className="info-item mb-3 pb-3 border-bottom">
                            <label className="text-muted small mb-1">
                                <FontAwesomeIcon icon="map-marker-alt" className="me-1" />
                                Dirección Fiscal
                            </label>
                            <div className="fw-semibold">{rnc.vDireccionFiscal}</div>
                        </div>
                    )}

                    <Row>
                        {rnc.vTelefono && (
                            <Col md={6}>
                                <div className="info-item mb-3 pb-3 border-bottom">
                                    <label className="text-muted small mb-1">
                                        <FontAwesomeIcon icon="phone" className="me-1" />
                                        Teléfono
                                    </label>
                                    <div className="fw-semibold">{rnc.vTelefono}</div>
                                </div>
                            </Col>
                        )}

                        {rnc.vEmail && (
                            <Col md={6}>
                                <div className="info-item mb-3 pb-3 border-bottom">
                                    <label className="text-muted small mb-1">
                                        <FontAwesomeIcon icon="envelope" className="me-1" />
                                        Email
                                    </label>
                                    <div className="fw-semibold">{rnc.vEmail}</div>
                                </div>
                            </Col>
                        )}
                    </Row>

                    <Row>
                        {rnc.vTipoContribuyente && (
                            <Col md={6}>
                                <div className="info-item mb-3 pb-3 border-bottom">
                                    <label className="text-muted small mb-1">Tipo de Contribuyente</label>
                                    <div className="fw-semibold">
                                        {tipoContribuyenteMap[rnc.vTipoContribuyente] || rnc.vTipoContribuyente}
                                    </div>
                                </div>
                            </Col>
                        )}

                        {rnc.vActividadEconomica && (
                            <Col md={6}>
                                <div className="info-item">
                                    <label className="text-muted small mb-1">Actividad Económica</label>
                                    <div className="fw-semibold">{rnc.vActividadEconomica}</div>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Credenciales de Acceso DGII */}
            <Card className="border-0 bg-light mb-3">
                <Card.Body>
                    <h6 className="text-primary mb-3">
                        <FontAwesomeIcon icon="key" className="me-2" />
                        Credenciales de Acceso
                    </h6>

                    <div className="info-item mb-3 pb-3 border-bottom">
                        <label className="text-muted small mb-1">Usuario</label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="fw-semibold flex-grow-1">
                                <code className="bg-white px-2 py-1 rounded fs-1">
                                    {rnc.vUsuario}
                                </code>
                            </div>
                            <CopyButton code={rnc.vUsuario} />
                        </div>
                    </div>

                    <div className="info-item">
                        <label className="text-muted small mb-1">Contraseña</label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="fw-semibold flex-grow-1">
                                <code className="bg-white px-2 py-1 rounded fs-1">{rnc.vPassword}</code>
                            </div>
                            <CopyButton code={rnc.vPassword} />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Configuración de Ambiente */}
            <Card className="border-0 bg-light mb-3">
                <Card.Body>
                    <h6 className="text-primary mb-3">
                        <FontAwesomeIcon icon="cog" className="me-2" />
                        Configuración de Ambiente
                    </h6>

                    {rnc.iAmbiente !== undefined && rnc.iAmbiente !== null && (
                        <div className="info-item mb-3 pb-3 border-bottom">
                            <label className="text-muted small mb-1">Ambiente</label>
                            <div className="fw-semibold">
                                <Badge bg={getAmbienteColor(rnc.iAmbiente)}>
                                    <FontAwesomeIcon icon="server" className="me-1" />
                                    {ambienteMap[rnc.iAmbiente] || `Ambiente ${rnc.iAmbiente}`}
                                </Badge>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Certificado Digital */}
            <Card className="border-0 bg-light mb-3">
                <Card.Body>
                    <h6 className="text-primary mb-3">
                        <FontAwesomeIcon icon="certificate" className="me-2" />
                        Certificado Digital
                    </h6>

                    {rnc.vCertificadoDGII ? (
                        <>
                            <div className="info-item mb-3 pb-3 border-bottom">
                                <label className="text-muted small mb-1">Estado del Certificado</label>
                                <div className="fw-semibold">
                                    <Badge 
                                        bg="success" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleDescargarCertificado}
                                        title="Clic para descargar el certificado"
                                    >
                                        <FontAwesomeIcon icon="check-circle" className="me-1" />
                                        Certificado cargado
                                        <FontAwesomeIcon icon="download" className="ms-2" />
                                    </Badge>
                                </div>
                                <small className="text-muted">Haga clic en el badge para descargar</small>
                            </div>

                            {rnc.vPasswordCertificado && (
                                <div className="info-item">
                                    <label className="text-muted small mb-1">Contraseña del Certificado</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="fw-semibold flex-grow-1">
                                            <code className="bg-white px-2 py-1 rounded fs-1">
                                                {'•'.repeat(rnc.vPasswordCertificado.length)}
                                            </code>
                                        </div>
                                        <CopyButton code={rnc.vPasswordCertificado} />
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="alert alert-warning mb-0">
                            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                            No se ha cargado ningún certificado digital
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Información de Plan y Notificación */}
            {(rnc.planActual || rnc.correoNotificacion || rnc.dFechaExpiracion) && (
                <Card className="border-0 bg-light mb-3">
                    <Card.Body>
                        <h6 className="text-primary mb-3">
                            <FontAwesomeIcon icon="calendar-check" className="me-2" />
                            Plan y Notificaciones
                        </h6>

                        {rnc.planActual && (
                            <div className="info-item mb-3 pb-3 border-bottom">
                                <label className="text-muted small mb-1">Plan actual</label>
                                <div className="fw-semibold">
                                    <Badge bg="info" className="me-2">
                                        <FontAwesomeIcon icon="star" className="me-1" />
                                        {rnc.planActual}
                                    </Badge>
                                </div>
                            </div>
                        )}

                        {rnc.correoNotificacion && (
                            <div className="info-item mb-3 pb-3 border-bottom">
                                <label className="text-muted small mb-1">Correo de notificación</label>
                                <div className="fw-semibold">
                                    <FontAwesomeIcon icon="envelope" className="me-2 text-muted" />
                                    {rnc.correoNotificacion}
                                </div>
                            </div>
                        )}

                        {rnc.dFechaExpiracion && (
                            <div className="info-item">
                                <label className="text-muted small mb-1">Fecha de expiración</label>
                                <div className="fw-semibold text-danger">
                                    <FontAwesomeIcon icon="calendar-alt" className="me-2" />
                                    {new Date(rnc.dFechaExpiracion).toLocaleDateString('es-DO', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            <div className="d-grid gap-2 mt-4">
                <Button
                    variant="primary"
                    onClick={onEdit}
                    size="sm"
                >
                    <FontAwesomeIcon icon="edit" className="me-2" />
                    Editar información
                </Button>
                <Button
                    variant="outline-success"
                    onClick={() => onAsignarFolios(rnc)}
                    size="sm"
                >
                    <FontAwesomeIcon icon="plus-circle" className="me-2" />
                    Asignar más folios
                </Button>
            </div>
        </div>
    );
};

TabGeneral.propTypes = {
    rnc: PropTypes.object,
    onEdit: PropTypes.func.isRequired,
    onAsignarFolios: PropTypes.func.isRequired
};

export default TabGeneral;
